import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Upload,
  X,
  Package,
  Settings as SettingsIcon,
  ExternalLink,
  Star,
} from "lucide-react";
import { api, resolveImg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { name: "", category: "", price: "", description: "", image_url: "", images: [], featured: false };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));

  useEffect(() => {
    loadProducts();
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) =>
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));

  const makeCover = (idx) =>
    setForm((f) => {
      const arr = [...(f.images || [])];
      const [x] = arr.splice(idx, 1);
      arr.unshift(x);
      return { ...f, images: arr };
    });

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    try {
      const imgs = form.images || [];
      const payload = {
        name: form.name,
        category: form.category,
        price: form.price,
        description: form.description,
        images: imgs,
        image_url: imgs[0] || form.image_url || "",
        featured: !!form.featured,
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setShowForm(false);
      loadProducts();
    } catch {
      toast.error("Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Product deleted");
    loadProducts();
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Brand details saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-obsidian-2 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-18 py-4 flex items-center justify-between">
          <div>
            <p className="font-heading text-2xl text-ink leading-none">Vee's Studio</p>
            <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-champagne mt-1">Admin Dashboard</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="font-accent text-xs tracking-[0.15em] uppercase text-ash hover:text-champagne transition-colors inline-flex items-center gap-2"
            >
              <ExternalLink size={14} /> View site
            </a>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="font-accent text-xs tracking-[0.15em] uppercase text-ash hover:text-red-400 transition-colors inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {/* Tabs */}
        <div className="flex gap-2 mb-10 border-b border-white/10">
          <button
            onClick={() => setTab("products")}
            data-testid="tab-products"
            className={`font-accent text-xs tracking-[0.2em] uppercase px-5 py-3 border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
              tab === "products" ? "border-champagne text-champagne" : "border-transparent text-ash hover:text-ink"
            }`}
          >
            <Package size={15} /> Products
          </button>
          <button
            onClick={() => setTab("settings")}
            data-testid="tab-settings"
            className={`font-accent text-xs tracking-[0.2em] uppercase px-5 py-3 border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
              tab === "settings" ? "border-champagne text-champagne" : "border-transparent text-ash hover:text-ink"
            }`}
          >
            <SettingsIcon size={15} /> Brand Details
          </button>
        </div>

        {tab === "products" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-3xl text-ink">
                Products <span className="text-ash text-lg">({products.length})</span>
              </h2>
              <button
                onClick={openNew}
                data-testid="add-product-btn"
                className="bg-champagne text-obsidian font-accent text-xs tracking-[0.2em] uppercase px-6 py-3 hover:bg-champagne-hover transition-colors inline-flex items-center gap-2"
              >
                <Plus size={16} /> Add Product
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-product-${p.id}`}
                  className="bg-obsidian-2 border border-white/10 overflow-hidden"
                >
                  <div className="aspect-[4/3] bg-obsidian-3 overflow-hidden relative">
                    {p.image_url ? (
                      <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-ash/40 text-xs">No image</div>
                    )}
                    {p.featured && (
                      <span className="absolute top-2 left-2 bg-champagne text-obsidian text-[10px] px-2 py-0.5 font-accent tracking-wide inline-flex items-center gap-1">
                        <Star size={10} /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-champagne/80">{p.category}</p>
                    <h3 className="font-heading text-xl text-ink mt-1">{p.name}</h3>
                    <p className="text-ash text-sm">{p.price}</p>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`edit-product-${p.id}`}
                        className="flex-1 border border-white/15 text-ink text-xs font-accent tracking-wide uppercase py-2.5 hover:border-champagne hover:text-champagne transition-colors inline-flex items-center justify-center gap-1.5"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => deleteProduct(p)}
                        data-testid={`delete-product-${p.id}`}
                        className="border border-white/15 text-ash py-2.5 px-3 hover:border-red-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} data-testid="settings-form" className="max-w-2xl">
            <h2 className="font-heading text-3xl text-ink mb-6">Brand Details</h2>
            {[
              ["brand_name", "Brand Name"],
              ["tagline", "Tagline"],
              ["owner_name", "Proprietor / Owner Name"],
              ["whatsapp_number", "WhatsApp Number (with country code, digits only)"],
              ["phone", "Phone Number (display, e.g. +232 75 386232)"],
              ["contact_email", "Contact Email"],
              ["address", "Address"],
              ["instagram", "Instagram Handle (without @)"],
            ].map(([key, label]) => (
              <div key={key} className="mb-5">
                <label className="block font-accent text-[10px] tracking-[0.25em] uppercase text-ash mb-2">{label}</label>
                <input
                  value={settings[key] || ""}
                  onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                  data-testid={`settings-${key}`}
                  className="w-full bg-obsidian-2 border border-white/10 text-ink px-4 py-3 focus:outline-none focus:border-champagne/60 transition-colors"
                />
              </div>
            ))}
            <div className="mb-6">
              <label className="block font-accent text-[10px] tracking-[0.25em] uppercase text-ash mb-2">About Text</label>
              <textarea
                rows={5}
                value={settings.about_text || ""}
                onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
                data-testid="settings-about_text"
                className="w-full bg-obsidian-2 border border-white/10 text-ink px-4 py-3 focus:outline-none focus:border-champagne/60 transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-settings-btn"
              className="bg-champagne text-obsidian font-accent text-xs tracking-[0.2em] uppercase px-8 py-3.5 hover:bg-champagne-hover transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Brand Details"}
            </button>
          </form>
        )}
      </div>

      {/* Product form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <form
            onSubmit={saveProduct}
            data-testid="product-form"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-obsidian-2 border border-white/10 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-ink">{editing ? "Edit Product" : "Add Product"}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-ash hover:text-ink">
                <X />
              </button>
            </div>

            {/* images */}
            <div className="mb-5">
              <label className="block font-accent text-[10px] tracking-[0.25em] uppercase text-ash mb-2">
                Product Images <span className="text-ash/50 normal-case tracking-normal">(first is the cover)</span>
              </label>

              {form.images && form.images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {form.images.map((src, i) => (
                    <div key={i} className="relative group aspect-[3/4] bg-obsidian-3 border border-white/10 overflow-hidden" data-testid={`form-image-${i}`}>
                      <img src={resolveImg(src)} alt={`img ${i + 1}`} className="w-full h-full object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-champagne text-obsidian text-[9px] px-1.5 py-0.5 font-accent tracking-wide">COVER</span>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {i !== 0 && (
                          <button
                            type="button"
                            onClick={() => makeCover(i)}
                            data-testid={`make-cover-${i}`}
                            className="text-[9px] font-accent tracking-wide uppercase text-champagne border border-champagne/50 px-2 py-1 hover:bg-champagne hover:text-obsidian transition-colors"
                          >
                            Cover
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          data-testid={`remove-image-${i}`}
                          className="text-ink hover:text-red-400 bg-black/50 p-1.5"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="aspect-[4/3] bg-obsidian-3 border border-white/10 mb-3 flex items-center justify-center">
                  <span className="text-ash/40 text-xs">No images yet</span>
                </div>
              )}

              <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="image-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-image-btn"
                className="w-full border border-white/15 text-ink font-accent text-xs tracking-wide uppercase py-3 hover:border-champagne hover:text-champagne transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Upload size={15} /> {uploading ? "Uploading…" : "Add Image(s)"}
              </button>
            </div>

            {[
              ["name", "Name", "text"],
              ["category", "Category", "text"],
              ["price", "Price (e.g. $185)", "text"],
            ].map(([key, label]) => (
              <div key={key} className="mb-4">
                <label className="block font-accent text-[10px] tracking-[0.25em] uppercase text-ash mb-2">{label}</label>
                <input
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`form-${key}`}
                  className="w-full bg-obsidian-3 border border-white/10 text-ink px-4 py-3 focus:outline-none focus:border-champagne/60 transition-colors"
                />
              </div>
            ))}

            <div className="mb-4">
              <label className="block font-accent text-[10px] tracking-[0.25em] uppercase text-ash mb-2">Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="form-description"
                className="w-full bg-obsidian-3 border border-white/10 text-ink px-4 py-3 focus:outline-none focus:border-champagne/60 transition-colors resize-none"
              />
            </div>

            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="form-featured"
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-ink text-sm">Show on homepage (Featured)</span>
            </label>

            <button
              type="submit"
              disabled={saving}
              data-testid="save-product-btn"
              className="w-full bg-champagne text-obsidian font-accent text-xs tracking-[0.2em] uppercase py-4 hover:bg-champagne-hover transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Update Product" : "Add Product"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
